/**
 * Answer a mission that needs you, in place (no trip to the Inbox): the shared answer of ADR 0072.
 * A held tool request or question is answered on its own card (SessionApprovals: the request card
 * with Allow once, Allow for session, Make it a rule, Deny; the question card for questions, plans
 * and forms). A session whose turn ended on a question takes a message, with the choices it listed
 * as quick replies that fill the box. A mission blocked by an expired or denied request offers
 * Approve and resume. A session Orgus cannot message says where to answer instead.
 */
import { ExternalLink } from '@orgus/ui/icons';
import { useNavigate } from 'react-router';
import type { Session } from '@orgus/protocol';
import { Button } from '@orgus/ui';
import { useApprovals } from '../approvals/live.ts';
import { ApproveAndResumeButton } from '../approvals/ApproveAndResumeButton.tsx';
import { SessionApprovals } from '../approvals/SessionApprovals.tsx';
import { useControlState } from '../control/live.ts';
import { SessionMessage } from '../control/SessionControl.tsx';
import { askViewOf } from '../session-detail/asks.ts';
import { fillReply } from '../session-detail/replyDraft.ts';
import type { MissionRow } from './model.ts';

export type AnswerRoute = { kind: 'resume'; approvalId: string } | { kind: 'held' } | { kind: 'message'; session: Session } | { kind: 'elsewhere'; session: Session } | null;

/** How this mission's question can be answered here, if at all. */
export function useAnswerRoute(row: MissionRow | null): AnswerRoute {
  const { approvals } = useApprovals();
  const control = useControlState();
  if (!row) return null;
  const ids = row.mission.sessionIds;
  // Blocked by a request that expired or was denied: Approve and resume, where the hub allows it.
  const blocked = row.view.blockers.find((b) => b.kind === 'approval' && b.outcome && b.resumable && b.approvalId && b.phase !== 'resuming');
  if (blocked) return { kind: 'resume', approvalId: blocked.approvalId! };
  if ((approvals?.pending ?? []).some((a) => a.state === 'pending' && ids.includes(a.sessionId))) return { kind: 'held' };
  const waiting = row.view.sessions.find((s) => s.state === 'waiting_for_human' && s.session)?.session ?? null;
  if (!waiting) return null;
  return control?.machines[waiting.machineId]?.installed && waiting.status !== 'ended' ? { kind: 'message', session: waiting } : { kind: 'elsewhere', session: waiting };
}

export function MissionAnswer({ row, route }: { row: MissionRow; route: AnswerRoute }) {
  const navigate = useNavigate();
  if (!route) return null;
  if (route.kind === 'resume') return <ApproveAndResumeButton approvalId={route.approvalId} variant="secondary" />;
  if (route.kind === 'held') return <SessionApprovals sessionIds={row.mission.sessionIds} />;
  const s = route.session;
  if (route.kind === 'elsewhere') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto text-ui text-fg-subtle">Orgus cannot message this session</span>
        <Button variant="secondary" size="sm" icon={<ExternalLink />} onClick={() => navigate(`/sessions?session=${encodeURIComponent(s.id)}`)}>
          Open session
        </Button>
      </div>
    );
  }
  const options = askViewOf(s, false).options;
  return (
    <div className="flex flex-col gap-3">
      {options.length > 0 && (
        <div role="group" aria-label="Quick replies" className="flex flex-wrap gap-2">
          {options.map((o) => (
            <Button key={o} size="sm" variant="secondary" className="max-w-full" onClick={() => fillReply(s.id, o)}>
              <span className="truncate">{o}</span>
            </Button>
          ))}
        </div>
      )}
      <SessionMessage session={s} />
    </div>
  );
}
